import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { Observable, Subscription, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ConfigService } from '../../my-shared-lib/src/lib/shared-services/config.service';
import { GlobalFilterService } from '../../my-shared-lib/src/lib/shared-services/global-filter-service';

type RowData = Record<string, any>;

interface ApiConfig {
  apiUrl?: string;
  url?: string;
  method?: string;
  payload?: any;
  customPayload?: any;
  responsePath?: string;
  responseKey?: string;
  displayField?: string;
  valueField?: string;
  includeCurrentFilters?: boolean;
  requestParams?: Record<string, any>;
  queryParams?: Record<string, any>;
  embedQueryParams?: boolean;
  embeddedParams?: string[];
  rawQueryString?: string;
  cacheByRow?: boolean;
  cacheKey?: string;
  alwaysCall?: boolean;
  retainExistingOnEmpty?: boolean;
  dummyData?: RowData[];
}

interface ColumnConfig {
  key: string;
  label?: string;
  type?: 'text' | 'input' | 'dropdown';
  freeze?: boolean;
  frozen?: boolean;
  sticky?: boolean;
  edit?: boolean;
  editable?: boolean;
  isDropdown?: boolean;
  options?: any[];
  optionsField?: string;
  isMultiSelect?: boolean;
  placeholder?: string;
  inputType?: string;
  minWidth?: number | string;
  width?: number | string;
  maxWidth?: number | string;
  apiUrl?: string;
  method?: string;
  payload?: any;
  responsePath?: string;
  responseKey?: string;
  displayField?: string;
  valueField?: string;
  dropdownApi?: ApiConfig;
  defaultToFirstOption?: boolean;
}

interface DropdownOption {
  label: string;
  value: any;
}

interface SelectionMapping {
  value?: string;
  triggerValue?: string;
  selectionValue?: string;
  row?: RowData;
  rows?: RowData[];
}

interface InteractiveSelectionConfig {
  enabled?: boolean;
  mode?: 'none' | 'row' | 'cell' | 'both';
  multiRow?: boolean;
  multiCell?: boolean;
  publishToWorkflowCtx?: boolean;
  rowCtxKey?: string;
  cellCtxKey?: string;
}

interface AddRowButtonConfig {
  enabled?: boolean;
  label?: string;
  insertAt?: 'start' | 'end' | 'top' | 'bottom';
  defaults?: RowData;
  row?: RowData;
  rowTemplate?: RowData;
}

interface FooterButtonConfig {
  enabled?: boolean;
  label?: string;
  marginLeft?: string;
  type?: string;
  isConflictStatusButton?: boolean;
  showWhenNoConflicts?: boolean;
  conflictColumnKey?: string;
  conflictTrueValues?: any[];
  conflictRequiresApplied?: boolean;
  conflictAppliedColumnKey?: string;
  conflictAppliedTrueValues?: any[];
  noConflictLabel?: string;
  conflictLabelTemplate?: string;
  disableWhenConflicts?: boolean;
  activationColumn?: string;
  enabledWhenAllValuesEqual?: any;
  submitApi?: FooterSubmitApiConfig | FooterSubmitApiConfig[];
  apiUrl?: string;
  method?: string;
  payload?: Record<string, any>;
  requestDataKey?: string;
  insertRequestKey?: string;
  rowFieldMap?: Record<string, string>;
  includeColumns?: string[];
  excludeColumns?: string[];
  includeCurrentFilters?: boolean;
  requestParams?: Record<string, any>;
  queryParams?: Record<string, any>;
  embedQueryParams?: boolean;
  embeddedParams?: string[];
  rawQueryString?: string;
  successMessage?: string;
  errorMessage?: string;
  disableAfterSuccess?: boolean;
  disabledStateWorkflowCtxKey?: string;
  reenableOnWorkflowCtxKey?: string | string[];
  clearReenableWorkflowCtxOnSuccess?: boolean;
}

interface FooterSubmitApiConfig extends ApiConfig {
  requestDataKey?: string;
  insertRequestKey?: string;
  rowFieldMap?: Record<string, string>;
  includeColumns?: string[];
  excludeColumns?: string[];
  sendDirtyRowsOnly?: boolean;
  onlyChangedRows?: boolean;
  successMessage?: string;
  errorMessage?: string;
  responseWidgetId?: string | string[];
  responseRowMap?: Record<string, string>;
  mergeByKey?: string;
  responseSummaryKey?: string;
  responseDetailKey?: string;
  successRoute?: string;
  replaceUrl?: boolean;
  skipTableData?: boolean;
}

@Component({
  selector: 'app-table-dropdown-api',
  standalone: true,
  imports: [CommonModule, FormsModule, MatTableModule, MatFormFieldModule, MatSelectModule, MatInputModule, MatIconModule, MatSnackBarModule],
  templateUrl: './table-dropdown-api.component.html',
  styleUrl: './table-dropdown-api.component.scss'
})
export class TableDropdownApiComponent implements OnDestroy {
  private _data: any = null;
  @Input() set data(value: any) {
    this._data = value;
    if (value && typeof value === 'object') {
      this.initialize();
    }
  }
  get data(): any {
    return this._data;
  }
  private subs = new Subscription();
  private baseRows: RowData[] = [];
  private mappedRowsByKey: Record<string, RowData[]> = {};
  private selectedMapKeys = new Set<string>();
  private cellConfigField = '__cellConfig';

  private dropdownOptionsByCell = new Map<string, DropdownOption[]>();
  private dropdownOptionsByCache = new Map<string, DropdownOption[]>();
  private dropdownLoadingCellKeys = new Set<string>();
  private dropdownPendingCellKeysByCache = new Map<string, Set<string>>();

  private interactiveSelectionMode: 'none' | 'row' | 'cell' | 'both' = 'none';
  private allowMultiRowSelection = false;
  private allowMultiCellSelection = false;
  private selectedRowIndexes = new Set<number>();
  private selectedCellKeys = new Set<string>();
  private enabledEditableColumns = new Set<string>();
  private hasUnsavedChanges = false;
  private dirtyRowIds = new Set<string>();
  private rowIdSequence = 0;
  private readonly internalRowIdField = '__row_local_id';
  private conflictCount = 0;
  private suppressSelfWidgetDataSync = false;
  private lastPublishedWidgetDataSignature = '';




  displayedColumns: string[] = [];
  columnLabels: Record<string, string> = {};
  columnConfigMap: Record<string, ColumnConfig> = {};
  dataSource: RowData[] = [];
  isOnInitDataLoading = false;
  loadingButtons = new Set<string>();
  private readonly defaultApprovalNoticeText = 'Sent for Approval';
  private readonly defaultTableViewportOffsetPx = 300;
  private readonly defaultTableMinHeightPx = 90;
  private readonly defaultTableMaxHeightPx = 560;
  private readonly tableHeaderHeightPx = 40;
  private readonly tableRowHeightPx = 48;
  private readonly horizontalScrollbarCompensationPx = 18;

  private readonly numericHints = ['discount', 'price', 'amount', 'sales', 'impact', 'total', 'qty', 'rate', 'percent', 'usd'];
  private readonly nonNumericHints = ['discount_type', 'type', 'reason', 'description', 'name', 'clause', 'condition', 'bundle'];
  private readonly textColumnKeys = new Set<string>([
    'customer_code',
    'customer_name',
    'part_number'
  ]);
  private readonly commaFreeTextColumnKeys = new Set<string>([
    'customer_code',
    'customer_name',
    'part_number'
  ]);

  constructor(
    private globalFilterService: GlobalFilterService,
    private configService: ConfigService,
    private router: Router,
    private snackBar: MatSnackBar
  ) { }

  get tableColumns(): string[] {
    this.ensureColumnsInitialized();
    return this.displayedColumns;
  }

  get shouldShowFooterLine(): boolean {
    const raw = this.data?.add_line ?? this.data?.addLine;
    if (typeof raw === 'string') {
      const n = raw.trim().toLowerCase();
      return n === 'true' || n === '1' || n === 'yes';
    }
    if (typeof raw === 'number') {
      return raw === 1;
    }
    return raw === true;
  }

  get footerLineText(): string {
    const configured = String(this.data?.add_line_text ?? this.data?.addLineText ?? '').trim();
    return configured || 'Approval triggers if cumulative discounts greater than 20%';
  }

  get isRowSelectionEnabled(): boolean {
    return this.interactiveSelectionMode === 'row' || this.interactiveSelectionMode === 'both';
  }

  get isCellSelectionEnabled(): boolean {
    return this.interactiveSelectionMode === 'cell' || this.interactiveSelectionMode === 'both';
  }

  get showAddRowButton(): boolean {
    const config = this.resolveAddRowButtonConfig();
    const explicit = this.parseBooleanFlag(config.enabled);
    if (explicit !== null) {
      return explicit;
    }

    const fallback = this.parseBooleanFlag(
      this.data?.enableAddRow ?? this.data?.allowManualRowAdd ?? this.data?.manualRowEntryEnabled
    );
    return fallback === true;
  }

  get addRowButtonLabel(): string {
    const config = this.resolveAddRowButtonConfig();
    const configured = String(
      config.label ?? this.data?.addRowLabel ?? this.data?.manualRowButtonLabel ?? ''
    ).trim();
    return configured || 'Add Row';
  }

  get tableViewportMaxHeightPx(): number | null {
    if (!this.isAutoTableHeightEnabled()) {
      return null;
    }
    return this.resolveEffectiveTableMaxHeightPx();
  }

  get tableViewportHeightPx(): number | null {
    if (!this.isAutoTableHeightEnabled()) {
      return null;
    }

    const effectiveMaxHeight = this.resolveEffectiveTableMaxHeightPx();
    const minHeight = Math.min(this.resolveConfiguredTableMinHeightPx(), effectiveMaxHeight);
    const rowsCount = Array.isArray(this.dataSource) ? this.dataSource.length : 0;
    const contentHeight =
      this.tableHeaderHeightPx +
      (rowsCount * this.tableRowHeightPx) +
      2 +
      this.horizontalScrollbarCompensationPx;
    return Math.max(minHeight, Math.min(contentHeight, effectiveMaxHeight));
  }

  get tableViewportOverflowY(): string | null {
    if (!this.isAutoTableHeightEnabled()) {
      return null;
    }

    const effectiveMaxHeight = this.resolveEffectiveTableMaxHeightPx();
    const rowsCount = Array.isArray(this.dataSource) ? this.dataSource.length : 0;
    const contentHeight =
      this.tableHeaderHeightPx +
      (rowsCount * this.tableRowHeightPx) +
      2 +
      this.horizontalScrollbarCompensationPx;

    return contentHeight > effectiveMaxHeight ? 'auto' : 'hidden';
  }

  get showFooterButton(): boolean {
    return this.footerButtonConfigs.length > 0;
  }

  get footerButtonConfigs(): FooterButtonConfig[] {
    return this.resolveFooterButtonConfigs().filter((config) => this.shouldRenderFooterButton(config));
  }

  getFooterButtonLabel(config: FooterButtonConfig): string {
    return String(config.label || '').trim() || 'Send';
  }

  getFooterButtonRenderLabel(config: FooterButtonConfig): string {
    if (!this.isConflictStatusButton(config)) {
      return this.loadingButtons.has(this.getFooterButtonLabel(config))
        ? 'Sending...'
        : this.getFooterButtonLabel(config);
    }

    if (this.conflictCount > 0) {
      const template = String(config.conflictLabelTemplate || '').trim();
      if (template) {
        return template.replace(/\{\{\s*count\s*\}\}/g, String(this.conflictCount));
      }

      const noun = this.conflictCount === 1 ? 'conflict' : 'conflicts';
      return `${this.conflictCount} ${noun} remaining`;
    }

    return String(config.noConflictLabel || '').trim() || 'No conflicts are there';
  }

  getFooterButtonClasses(config: FooterButtonConfig): Record<string, boolean> {
    const isStatusButton = this.isConflictStatusButton(config);
    return {
      'conflict-status-button': isStatusButton,
      'conflicts-present': isStatusButton && this.conflictCount > 0,
      'no-conflicts': isStatusButton && this.conflictCount === 0
    };
  }

  private shouldRenderFooterButton(config: FooterButtonConfig): boolean {
    if (!config || typeof config !== 'object') {
      return false;
    }

    const explicit = this.parseBooleanFlag(config.enabled);
    if (explicit === false) {
      return false;
    }

    if (this.isConflictStatusButton(config)) {
      const showWhenNoConflicts = this.parseBooleanFlag(config.showWhenNoConflicts);
      if (this.conflictCount <= 0 && showWhenNoConflicts !== true) {
        return false;
      }
      return true;
    }

    if (explicit === true) {
      return true;
    }

    return !!String(config.label || '').trim();
  }

  isConflictRow(row: RowData): boolean {
    if (!row || typeof row !== 'object' || Array.isArray(row)) {
      return false;
    }

    const config = this.getConflictStatusButtonConfig();
    return this.isRowInConflict(row, config || undefined);
  }

  isFooterButtonDisabled(config: FooterButtonConfig): boolean {
    if (this.isConflictStatusButton(config)) {
      return false;
    }


    if (this.isFooterButtonWorkflowLocked(config)) {
      if (this.parseBooleanFlag(config.disableWhenConflicts) === true && this.conflictCount > 0) {
        return true;
      }

      const buttonLabel = this.getFooterButtonLabel(config);
      if (buttonLabel === 'Save') {
        return !this.hasUnsavedChanges;
      }

      const activationColumn = String(config.activationColumn || '').trim();
      const expectedValue = this.normalizeFooterButtonValue(config.enabledWhenAllValuesEqual);

      if (!activationColumn || !expectedValue) {
        return false;
      }

      if (!Array.isArray(this.dataSource) || this.dataSource.length === 0) {
        return true;
      }

      return this.dataSource.some((row) => {
        const currentValue = this.normalizeFooterButtonValue(row?.[activationColumn]);
        return currentValue !== expectedValue;
      });
    }

    return false;
  }

  onAddRowClick(event?: MouseEvent): void {
    event?.stopPropagation();
    if (!this.showAddRowButton) {
      return;
    }

    const config = this.resolveAddRowButtonConfig();
    const manualRow = this.createManualRow(config);
    this.markRowAsDirty(manualRow);
    const insertAtStart = this.shouldInsertManualRowAtStart(config);

    if (insertAtStart) {
      this.baseRows = [manualRow, ...this.baseRows];
      this.selectedRowIndexes.clear();
      this.selectedCellKeys.clear();
    } else {
      this.baseRows = [...this.baseRows, manualRow];
    }

    this.rebuildWithMappings();
    this.hasUnsavedChanges = true;
  }

  onFooterButtonClick(config: FooterButtonConfig, event?: MouseEvent): void {
    event?.stopPropagation();
    if (this.isConflictStatusButton(config)) {
      return;
    }

    const buttonLabel = this.getFooterButtonLabel(config);
    if (this.isFooterButtonDisabled(config) || this.loadingButtons.has(buttonLabel)) {
      return;
    }

    const submitApiConfigs = this.resolveFooterSubmitApiConfigs(config);
    if (!submitApiConfigs || submitApiConfigs.length === 0) {
      this.showApprovalNotification(this.defaultApprovalNoticeText);
      return;
    }

    this.loadingButtons.add(buttonLabel);
    this.processSubmitApiConfigs(config, submitApiConfigs, 0, buttonLabel);
  }

  private processSubmitApiConfigs(
    footerButtonConfig: FooterButtonConfig,
    configs: FooterSubmitApiConfig[],
    index: number,
    buttonLabel: string
  ): void {
    if (index >= configs.length) {
      this.loadingButtons.delete(buttonLabel);
      return;
    }

    const submitApiConfig = configs[index];
    const payload = this.buildFooterSubmitPayload(submitApiConfig);
    const apiRequest = this.buildFooterSubmitRequest(submitApiConfig, payload);
    if (!apiRequest) {
      this.showApprovalNotification(String(submitApiConfig.errorMessage || 'Footer API is not configured.'));
      this.loadingButtons.delete(buttonLabel);
      return;
    }

    const successMessage = String(submitApiConfig.successMessage || this.defaultApprovalNoticeText).trim() || this.defaultApprovalNoticeText;
    const errorMessage = String(submitApiConfig.errorMessage || 'Failed to send approval request.').trim() || 'Failed to send approval request.';
    const shouldResetDirtyState = configs.some((cfg) => this.shouldSendDirtyRowsOnly(cfg));

    const sub = apiRequest
      .pipe(finalize(() => {
        // Don't set loading to false here, as we might have more calls
      }))
      .subscribe({
        next: (response) => {
          this.refreshConflictStatusFromResponse(response, submitApiConfig);
          this.syncFooterResponseToWidget(response, submitApiConfig);
          if (index === configs.length - 1) {
            // Last API call
            if (buttonLabel === 'Save' || shouldResetDirtyState) {
              this.hasUnsavedChanges = false;
            }
            if (shouldResetDirtyState) {
              this.clearDirtyRows();
            }
            this.navigateAfterFooterSubmit(submitApiConfig);
            this.updateFooterButtonSuccessState(footerButtonConfig);
            this.showApprovalNotification(successMessage);
            this.loadingButtons.delete(buttonLabel);
          } else {
            // More calls to make (no artificial delay between chained API calls)
            this.processSubmitApiConfigs(footerButtonConfig, configs, index + 1, buttonLabel);
          }
        },
        error: (error) => {
          console.error('[table-dropdown-api] Footer API call failed', error);
          this.showApprovalNotification(errorMessage);
          this.loadingButtons.delete(buttonLabel);
        }
      });
    this.subs.add(sub);
  }

  formatHeaderLabel(value: any): string {
    const raw = String(value ?? '').trim();
    if (!raw) {
      return '';
    }
    const normalized = raw.replace(/[_-]+/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/\s+/g, ' ').trim();
    return normalized
      .split(' ')
      .map((word) => word ? (word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) : word)
      .join(' ');
  }

  getColumnStyle(columnKey: string, row?: RowData): Record<string, string> {
    const config = this.getCellConfig(row, columnKey);
    const style: Record<string, string> = {};
    const min = this.toCssSize((config as any)?.minWidth ?? (config as any)?.min_width);
    const width = this.toCssSize(config?.width);
    const max = this.toCssSize((config as any)?.maxWidth ?? (config as any)?.max_width);
    if (min) style['min-width'] = min;
    if (width) style['width'] = width;
    if (max) style['max-width'] = max;
    return style;
  }

  getHeaderCellClasses(columnKey: string): Record<string, boolean> {
    const frozen = this.isFrozenColumn(columnKey);
    return {
      'numeric-column': this.isNumericColumn(columnKey),
      'frozen-column': frozen,
      'non-frozen-column': !frozen
    };
  }

  getBodyCellClasses(columnKey: string, row: RowData, rowIndex: number): Record<string, boolean> {
    const frozen = this.isFrozenColumn(columnKey);
    return {
      'numeric-column': this.isNumericColumn(columnKey, row),
      'dropdown-column': this.isDropdownColumn(columnKey, row),
      'input-column': this.isInputColumn(columnKey, row),
      'cell-selected': this.isCellSelected(rowIndex, columnKey),
      'frozen-column': frozen,
      'non-frozen-column': !frozen
    };
  }

  isFrozenColumn(columnKey: string): boolean {
    const orderedColumns = this.getConfiguredColumns();
    const targetIndex = orderedColumns.indexOf(columnKey);
    if (targetIndex < 0) {
      return this.isColumnConfiguredFrozen(columnKey);
    }

    // Excel-like freeze pane behavior: only a left contiguous block is frozen.
    for (let idx = 0; idx <= targetIndex; idx += 1) {
      if (!this.isColumnConfiguredFrozen(orderedColumns[idx])) {
        return false;
      }
    }

    return true;
  }

  private isColumnConfiguredFrozen(columnKey: string): boolean {
    const config = this.columnConfigMap[columnKey] || { key: columnKey };
    const rawValue = (config as any).freeze ?? (config as any).frozen ?? (config as any).sticky;
    return this.parseBooleanFlag(rawValue) === true;
  }

  isDropdownColumn(columnKey: string, row?: RowData): boolean {
    const config = this.getCellConfig(row, columnKey);
    return config?.type === 'dropdown' || config?.isDropdown === true || !!this.resolveDropdownApiConfig(config);
  }

  private hasExplicitEditFlag(config?: ColumnConfig): boolean {
    return typeof config?.edit === 'boolean' || typeof config?.editable === 'boolean';
  }

  isColumnEditable(columnKey: string, row?: RowData): boolean {
    const config = this.getCellConfig(row, columnKey);
    if (typeof config?.edit === 'boolean') {
      return config.edit ? this.enabledEditableColumns.has(columnKey) : false;
    }

    if (typeof config?.editable === 'boolean') {
      return config.editable ? this.enabledEditableColumns.has(columnKey) : false;
    }

    return true;
  }

  canToggleColumnEdit(columnKey: string): boolean {
    const config = this.columnConfigMap[columnKey];
    if (!config) {
      return false;
    }

    if (typeof config.edit === 'boolean') {
      return config.edit === true;
    }

    if (typeof config.editable === 'boolean') {
      return config.editable === true;
    }

    return false;
  }

  isColumnEditEnabled(columnKey: string): boolean {
    return this.enabledEditableColumns.has(columnKey);
  }

  toggleColumnEdit(columnKey: string, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!this.canToggleColumnEdit(columnKey)) {
      return;
    }

    if (this.enabledEditableColumns.has(columnKey)) {
      this.enabledEditableColumns.delete(columnKey);
      return;
    }

    this.enabledEditableColumns.add(columnKey);
  }

  isInputColumn(columnKey: string, row?: RowData): boolean {
    const config = this.getCellConfig(row, columnKey);
    const type = String(config?.type || '').trim().toLowerCase();
    if (type === 'dropdown' || config?.isDropdown === true || !!this.resolveDropdownApiConfig(config)) return false;
    if (type === 'input') return true;
    if ((config as any)?.isInput === true) return true;
    if (!!String(config?.inputType || '').trim()) return true;
    return this.hasExplicitEditFlag(config) && this.isColumnEditable(columnKey, row);
  }

  isMultiSelect(columnKey: string, row?: RowData): boolean {
    return this.getCellConfig(row, columnKey)?.isMultiSelect === true;
  }

  getInputType(columnKey: string, row?: RowData): string {
    if (this.isNumericColumn(columnKey, row)) {
      return 'text';
    }
    return String(this.getCellConfig(row, columnKey)?.inputType || 'text');
  }

  getInputPlaceholder(columnKey: string, row?: RowData): string {
    const configured = String(this.getCellConfig(row, columnKey)?.placeholder || '').trim();
    if (configured) return configured;
    const label = String(this.columnLabels[columnKey] || columnKey || '').trim();
    return label ? `Enter ${label}` : 'Enter value';
  }

  getDropdownPlaceholder(columnKey: string, row?: RowData): string {
    const configured = String(this.getCellConfig(row, columnKey)?.placeholder || '').trim();
    if (configured) return configured;
    const label = String(this.columnLabels[columnKey] || columnKey || '').trim();
    return label ? `Select ${label}` : 'Select';
  }

  getInputDisplayValue(columnKey: string, value: any, row?: RowData): any {
    if (!this.isNumericColumn(columnKey, row)) return value ?? '';
    return this.isPercentageColumn(columnKey) ? this.formatPercentageForDisplay(value) : this.formatNumericForDisplay(value);
  }

  public getCellDisplayValue(columnKey: string, value: any, row?: RowData): any {
    if (!this.isNumericColumn(columnKey, row)) return value ?? '';
    return this.isPercentageColumn(columnKey) ? this.formatPercentageForDisplay(value) : this.formatNumericForDisplay(value);
  }

  getDropdownOptions(row: RowData, columnKey: string, rowIndex: number): DropdownOption[] {
    const config = this.getCellConfig(row, columnKey);
    const rowOptionsField = config.optionsField || `${columnKey}Options`;
    const rowOptions = Array.isArray(row?.[rowOptionsField]) ? row[rowOptionsField] : [];
    const staticOptions = Array.isArray(config.options) ? config.options : [];
    const apiOptions = this.getApiOptionsForCell(row, columnKey, rowIndex, config);
    return this.dedupeOptions([
      ...this.normalizeOptions(rowOptions, config),
      ...this.normalizeOptions(staticOptions, config),
      ...apiOptions
    ]);
  }

  isDropdownLoading(row: RowData, columnKey: string, rowIndex: number): boolean {
    const config = this.getCellConfig(row, columnKey);
    const apiConfig = this.resolveDropdownApiConfig(config);
    if (!apiConfig) {
      return false;
    }

    const cellKey = this.cellKey(rowIndex, columnKey);
    if (this.dropdownLoadingCellKeys.has(cellKey)) {
      return true;
    }

    const cacheKey = this.buildApiCacheKey(apiConfig, row);
    return this.dropdownPendingCellKeysByCache.has(cacheKey);
  }

  public onDropdownChange(row: RowData, columnKey: string, value: any): void {
    if (!this.isColumnEditable(columnKey, row)) {
      return;
    }

    row[columnKey] = this.normalizeDropdownValue(columnKey, value, row);
    this.syncConflictFlagWithApplied(row, columnKey);
    this.markRowAsDirty(row);
    this.hasUnsavedChanges = true;
    this.syncWorkflowCtx(columnKey, row[columnKey]);
    this.publishWidgetData();
    this.refreshConflictStatusFromDataSource();
  }

  onDropdownOpened(opened: boolean, row: RowData, columnKey: string, rowIndex: number): void {
    if (!opened) {
      return;
    }

    if (!this.isColumnEditable(columnKey, row)) {
      return;
    }

    const config = this.getCellConfig(row, columnKey);
    const apiConfig = this.resolveDropdownApiConfig(config);
    if (!apiConfig) {
      return;
    }

    this.loadDropdownOptionsForCell(row, rowIndex, columnKey, config, apiConfig);
  }

  onTextInputChange(row: RowData, columnKey: string, value: any): void {
    if (!this.isColumnEditable(columnKey, row)) {
      return;
    }

    const nextValue = this.isNumericColumn(columnKey, row) ? this.normalizeNumeric(value) : (value ?? '');
    row[columnKey] = nextValue;
    this.syncConflictFlagWithApplied(row, columnKey);
    this.markRowAsDirty(row);
    this.hasUnsavedChanges = true;
    this.syncWorkflowCtx(columnKey, nextValue);
    this.publishWidgetData();
    this.refreshConflictStatusFromDataSource();
  }

  public onRowSelectionToggle(rowIndex: number): void {
    if (!this.isRowSelectionEnabled) return;
    if (this.selectedRowIndexes.has(rowIndex)) {
      this.selectedRowIndexes.delete(rowIndex);
    } else {
      if (!this.allowMultiRowSelection) this.selectedRowIndexes.clear();
      this.selectedRowIndexes.add(rowIndex);
    }
    this.publishInteractiveSelection();
  }

  onCellSelectionToggle(rowIndex: number, columnKey: string, event: MouseEvent): void {
    if (!this.isCellSelectionEnabled) return;
    event.stopPropagation();
    const key = this.cellKey(rowIndex, columnKey);
    if (this.selectedCellKeys.has(key)) {
      this.selectedCellKeys.delete(key);
    } else {
      if (!this.allowMultiCellSelection) this.selectedCellKeys.clear();
      this.selectedCellKeys.add(key);
    }
    this.publishInteractiveSelection();
  }

  isRowSelected(rowIndex: number): boolean {
    return this.selectedRowIndexes.has(rowIndex);
  }

  isCellSelected(rowIndex: number, columnKey: string): boolean {
    return this.selectedCellKeys.has(this.cellKey(rowIndex, columnKey));
  }

  private initialize(): void {
    this.subs.unsubscribe();
    this.subs = new Subscription();
    this.clearVolatileState();
    this.hideApprovalNotification();

    const headerColumns = this.normalizeColumns(this.data?.headerColumns);
    this.displayedColumns = headerColumns.map((c) => c.key);
    this.columnLabels = Object.fromEntries(headerColumns.map((c) => [c.key, c.label || c.key]));
    this.columnConfigMap = Object.fromEntries(headerColumns.map((c) => [c.key, { ...c }]));
    this.cellConfigField = String(this.data?.cellConfigField || '__cellConfig').trim() || '__cellConfig';
    this.configureInteractiveSelection();
    this.buildMappedRowsConfig();

    const configuredRows = Array.isArray(this.data?.tableData) ? this.data.tableData : (Array.isArray(this.data?.rows) ? this.data.rows : []);
    this.baseRows = configuredRows.map((row: any) => this.buildRow({ ...(row || {}) }));
    this.dataSource = this.baseRows.map((row) => ({ ...row }));
    this.normalizeDropdownValues(this.dataSource);
    this.prefillFromWorkflowCtx(this.dataSource);

    this.hasUnsavedChanges = false;
    this.clearDirtyRows();

    this.restoreWidgetDataIfAny();
    this.bindFilterMappings();
    this.bindResetHandler();
    this.loadOnInitDataApi();
    if (this.shouldPreloadDropdownOptions()) {
      this.preloadDropdownApis(this.dataSource);
    }
    this.publishWidgetData();
    this.refreshConflictStatusFromDataSource();
  }

  private clearVolatileState(): void {
    this.selectedMapKeys.clear();
    this.dropdownOptionsByCell.clear();
    this.dropdownOptionsByCache.clear();
    this.dropdownLoadingCellKeys.clear();
    this.dropdownPendingCellKeysByCache.clear();
    this.selectedRowIndexes.clear();
    this.selectedCellKeys.clear();
    this.enabledEditableColumns.clear();
    this.clearDirtyRows();
    this.rowIdSequence = 0;
    this.suppressSelfWidgetDataSync = false;
    this.lastPublishedWidgetDataSignature = '';
  }

  private showApprovalNotification(message?: string): void {
    const approvalMessage = String(message || this.defaultApprovalNoticeText).trim() || this.defaultApprovalNoticeText;
    this.snackBar.dismiss();
    this.snackBar.open(approvalMessage, 'Close', {
      duration: 4000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['footer-approval-snackbar']
    });
  }

  private hideApprovalNotification(): void {
    this.snackBar.dismiss();
  }

  private normalizeColumns(columns: any[]): ColumnConfig[] {
    const source = Array.isArray(columns) ? columns : [];
    return source
      .filter((c) => c && typeof c === 'object')
      .map((c) => ({ ...c, key: String(c.key || '').trim() }))
      .filter((c) => !!c.key);
  }

  private ensureColumnsInitialized(): void {
    if (this.displayedColumns.length > 0) {
      return;
    }

    const headerColumns = this.normalizeColumns(this.data?.headerColumns);
    if (!headerColumns.length) {
      return;
    }

    this.displayedColumns = headerColumns.map((c) => c.key);
    this.columnLabels = Object.fromEntries(headerColumns.map((c) => [c.key, c.label || c.key]));
    this.columnConfigMap = Object.fromEntries(headerColumns.map((c) => [c.key, { ...c }]));
  }

  private getConfiguredColumns(): string[] {
    this.ensureColumnsInitialized();
    return this.displayedColumns;
  }

  private resolveAddRowButtonConfig(): AddRowButtonConfig {
    const rawConfig = this.data?.addRowButton ?? this.data?.manualRowButton ?? this.data?.addRow;
    if (rawConfig === true) {
      return { enabled: true };
    }

    if (typeof rawConfig === 'string') {
      const label = rawConfig.trim();
      return {
        enabled: true,
        ...(label ? { label } : {})
      };
    }

    if (rawConfig && typeof rawConfig === 'object' && !Array.isArray(rawConfig)) {
      return rawConfig as AddRowButtonConfig;
    }

    return {};
  }

  private resolveFooterButtonConfigs(): FooterButtonConfig[] {
    const rawConfig = this.data?.footerButtons ?? this.data?.footerButton ?? this.data?.bottomButton ?? this.data?.actionButton;
    if (rawConfig === true) {
      return [{ enabled: true }];
    }

    if (typeof rawConfig === 'string') {
      const label = rawConfig.trim();
      return [{
        enabled: true,
        ...(label ? { label } : {})
      }];
    }

    if (rawConfig && typeof rawConfig === 'object') {
      if (Array.isArray(rawConfig)) {
        return rawConfig as FooterButtonConfig[];
      } else {
        return [rawConfig as FooterButtonConfig];
      }
    }

    return [];
  }

  private parseBooleanFlag(value: any): boolean | null {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      if (value === 1) {
        return true;
      }
      if (value === 0) {
        return false;
      }
      return null;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
        return true;
      }
      if (normalized === 'false' || normalized === '0' || normalized === 'no') {
        return false;
      }
    }

    return null;
  }

  private normalizeWorkflowCtxKeys(value: string | string[] | undefined): string[] {
    if (Array.isArray(value)) {
      return value
        .map((entry) => String(entry || '').trim())
        .filter((entry) => !!entry);
    }

    const key = String(value || '').trim();
    return key ? [key] : [];
  }

  private isWorkflowCtxValuePresent(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      return value !== 0;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return !!normalized && normalized !== 'false' && normalized !== '0' && normalized !== 'null' && normalized !== 'undefined';
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === 'object') {
      return Object.keys(value).length > 0;
    }

    return true;
  }

  private isFooterButtonWorkflowLocked(config: FooterButtonConfig): boolean {
    const lockKey = String(config.disabledStateWorkflowCtxKey || '').trim();
    if (!lockKey) {
      return false;
    }

    const ctx = this.globalFilterService.getWorkflowCtx<Record<string, any>>() || {};
    const isLocked = this.parseBooleanFlag(ctx[lockKey]) === true || ctx[lockKey] === true;
    if (!isLocked) {
      return false;
    }

    const unlockKeys = this.normalizeWorkflowCtxKeys(config.reenableOnWorkflowCtxKey);
    if (!unlockKeys.length) {
      return true;
    }

    return !unlockKeys.some((key) => this.isWorkflowCtxValuePresent(ctx[key]));
  }

  private updateFooterButtonSuccessState(config: FooterButtonConfig): void {
    if (this.parseBooleanFlag(config.disableAfterSuccess) !== true) {
      return;
    }

    const lockKey = String(config.disabledStateWorkflowCtxKey || '').trim();
    if (!lockKey) {
      return;
    }

    const nextCtx: Record<string, any> = {
      [lockKey]: true
    };

    if (this.parseBooleanFlag(config.clearReenableWorkflowCtxOnSuccess) === true) {
      this.normalizeWorkflowCtxKeys(config.reenableOnWorkflowCtxKey)
        .forEach((key) => {
          nextCtx[key] = null;
        });
    }

    this.globalFilterService.setWorkflowCtx(nextCtx);
  }

  private isAutoTableHeightEnabled(): boolean {
    const explicit = this.parseBooleanFlag(
      this.data?.tableAutoHeight ??
      this.data?.autoTableHeight ??
      this.data?.adaptiveTableHeight
    );
    return explicit !== false;
  }

  private resolveEffectiveTableMaxHeightPx(): number {
    const viewportHeight = this.resolveViewportHeightPx();
    const viewportOffset = this.toPositiveNumber(
      this.data?.tableViewportOffsetPx ??
      this.data?.containerViewportOffsetPx ??
      this.data?.tableHeightOffsetPx ??
      this.data?.viewportOffsetPx
    ) ?? this.defaultTableViewportOffsetPx;
    const configuredMax = this.toPositiveNumber(
      this.data?.tableMaxHeightPx ??
      this.data?.maxTableHeightPx
    ) ?? this.defaultTableMaxHeightPx;
    const minHeight = this.resolveConfiguredTableMinHeightPx();
    const viewportBoundMax = Math.max(minHeight, viewportHeight - viewportOffset);
    return Math.max(minHeight, Math.min(configuredMax, viewportBoundMax));
  }

  private resolveConfiguredTableMinHeightPx(): number {
    return this.toPositiveNumber(this.data?.tableMinHeightPx ?? this.data?.minTableHeightPx)
      ?? this.defaultTableMinHeightPx;
  }

  private resolveViewportHeightPx(): number {
    if (typeof window !== 'undefined' && Number.isFinite(window.innerHeight)) {
      return window.innerHeight;
    }
    return 900;
  }

  private toPositiveNumber(value: any): number | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      return null;
    }
    return numericValue;
  }

  private normalizeFooterButtonValue(value: any): string {
    return String(value ?? '').trim().toLowerCase();
  }

  private shouldInsertManualRowAtStart(config: AddRowButtonConfig): boolean {
    const insertAt = String(config.insertAt ?? this.data?.addRowInsertAt ?? '').trim().toLowerCase();
    return insertAt === 'start' || insertAt === 'top';
  }

  private resolveManualRowDefaults(config: AddRowButtonConfig): RowData {
    const defaults = config.defaults ?? config.row ?? config.rowTemplate ?? this.data?.addRowDefaults;
    if (defaults && typeof defaults === 'object' && !Array.isArray(defaults)) {
      return { ...(defaults as RowData) };
    }
    return {};
  }

  private createManualRow(config: AddRowButtonConfig): RowData {
    const row = this.buildRow(this.resolveManualRowDefaults(config));
    this.getConfiguredColumns().forEach((columnKey) => {
      const currentValue = row[columnKey];
      if (this.isDropdownColumn(columnKey, row)) {
        row[columnKey] = this.normalizeDropdownValue(columnKey, currentValue, row);
        return;
      }

      if (this.isNumericColumn(columnKey, row)) {
        row[columnKey] = this.normalizeNumeric(currentValue);
        return;
      }

      row[columnKey] = currentValue ?? '';
    });

    return row;
  }

  private buildMappedRowsConfig(): void {
    this.mappedRowsByKey = {};
    const genericMappings = Array.isArray(this.data?.selectionMappings) ? this.data.selectionMappings as SelectionMapping[] : [];
    genericMappings.forEach((mapping) => {
      const trigger = String(mapping?.value ?? mapping?.triggerValue ?? mapping?.selectionValue ?? '').trim();
      if (!trigger) return;
      const rows = Array.isArray(mapping.rows) ? mapping.rows : (mapping.row ? [mapping.row] : []);
      this.mappedRowsByKey[this.normalizeKey(trigger)] = rows
        .filter((row) => row && typeof row === 'object')
        .map((row) => this.buildRow({ ...row }));
    });

    if (Object.keys(this.mappedRowsByKey).length > 0) return;

    const discountMappings = Array.isArray(this.data?.discountTypeMappings) ? this.data.discountTypeMappings : [];
    discountMappings.forEach((mapping: any) => {
      const discountType = String(mapping?.discount_type ?? mapping?.discountType ?? '').trim();
      if (!discountType) return;
      const row: RowData = {
        [this.data?.discountTypeColumnKey || 'discount_type']: discountType,
        [this.data?.quoteLevelDiscountColumnKey || 'quote_level_discount']: String(mapping?.quote_level_discount ?? mapping?.quoteLevelDiscount ?? '').trim(),
        [this.data?.reasonColumnKey || 'reason']: String(mapping?.defaultSelectedReason ?? '').trim()
      };
      const reasons = Array.isArray(mapping?.reasons) ? mapping.reasons : [];
      const reasonColumn = this.data?.reasonColumnKey || 'reason';
      row[`${reasonColumn}Options`] = reasons;
      this.mappedRowsByKey[this.normalizeKey(discountType)] = [this.buildRow(row)];
    });
  }

  private buildRow(row: RowData): RowData {
    const output = { ...row };
    this.ensureInternalRowId(output);
    this.getConfiguredColumns().forEach((key) => {
      if (output[key] === undefined || output[key] === null) {
        output[key] = '';
        return;
      }

      if (this.isCommaFreeTextColumn(key)) {
        output[key] = this.normalizeCommaFreeTextValue(output[key]);
      }
    });
    return output;
  }

  private isCommaFreeTextColumn(columnKey: string): boolean {
    return this.commaFreeTextColumnKeys.has(String(columnKey || '').trim().toLowerCase());
  }

  private normalizeCommaFreeTextValue(value: any): string {
    if (value === undefined || value === null) {
      return '';
    }

    return String(value).replace(/,/g, '').trim();
  }

  private createEmptyRow(): RowData {
    const row: RowData = {};
    this.getConfiguredColumns().forEach((columnKey) => {
      row[columnKey] = this.isMultiSelect(columnKey) ? [] : '';
    });
    this.ensureInternalRowId(row);
    return row;
  }

  private bindFilterMappings(): void {
    const filterKey = String(this.data?.filterKey || this.data?.discountTypeFilterKey || '').trim();
    if (!filterKey || Object.keys(this.mappedRowsByKey).length === 0) return;

    const applyFromFilters = (filters: any) => {
      if (filters?.changedFilterKey && filters.changedFilterKey !== filterKey) return;
      const selectedValues = this.extractFilterValues(filters, filterKey);
      if (this.data?.retainOnEmptySelection === true && selectedValues.length === 0) return;
      this.selectedMapKeys = new Set(selectedValues.map((v) => this.normalizeKey(v)).filter((v) => !!v));
      this.rebuildWithMappings();
    };

    const sub = this.globalFilterService.filter$.subscribe(applyFromFilters);
    this.subs.add(sub);
    applyFromFilters(this.globalFilterService.getCurrentFilters());
  }

  private rebuildWithMappings(): void {
    const mappedRows = Array.from(this.selectedMapKeys)
      .flatMap((key) => this.mappedRowsByKey[key] || [])
      .map((row) => ({ ...row }));

    this.dataSource = this.dedupeRows([...this.baseRows.map((row) => ({ ...row })), ...mappedRows]);
    this.normalizeDropdownValues(this.dataSource);
    this.prefillFromWorkflowCtx(this.dataSource);
    if (this.shouldPreloadDropdownOptions()) {
      this.preloadDropdownApis(this.dataSource);
    }
    this.trimInteractiveSelection();
    this.publishWidgetData();
    this.refreshConflictStatusFromDataSource();
  }

  private bindResetHandler(): void {
    const sub = this.globalFilterService.resetState$.subscribe(() => {
      this.selectedMapKeys.clear();
      this.dataSource = this.baseRows.map((row) => {
        const resetRow: RowData = {};
        this.getConfiguredColumns().forEach((column) => {
          resetRow[column] = this.isMultiSelect(column) ? [] : '';
        });
        return resetRow;
      });
      this.selectedRowIndexes.clear();
      this.selectedCellKeys.clear();
      this.publishInteractiveSelection();
      this.publishWidgetData();
    });
    this.subs.add(sub);
  }

  private restoreWidgetDataIfAny(): void {
    if (!this.data?.useWidgetData || !this.data?.widgetId) return;

    const existing = this.globalFilterService.getWidgetData(this.data.widgetId);
    if (existing.length > 0) {
      this.applyExternalWidgetRows(existing);
    }

    const sub = this.globalFilterService.widgetData$.subscribe((map) => {
      const rows = map?.[this.data.widgetId];
      if (!Array.isArray(rows)) return;

      const normalizedRows = rows.map((row) => this.buildRow({ ...(row || {}) }));
      const incomingSignature = this.buildRowsSignature(normalizedRows);
      if (this.suppressSelfWidgetDataSync && incomingSignature === this.lastPublishedWidgetDataSignature) {
        return;
      }

      this.applyExternalWidgetRows(normalizedRows);
    });
    this.subs.add(sub);
  }

  private applyExternalWidgetRows(rows: RowData[]): void {
    const normalizedRows = Array.isArray(rows)
      ? rows.map((row) => this.buildRow({ ...(row || {}) }))
      : [];
    const incomingSignature = this.buildRowsSignature(normalizedRows);
    const currentSignature = this.buildRowsSignature(this.dataSource);
    if (incomingSignature === currentSignature) {
      return;
    }

    this.dataSource = this.dedupeRows(normalizedRows);
    this.normalizeDropdownValues(this.dataSource);
    this.prefillFromWorkflowCtx(this.dataSource);
    if (this.shouldPreloadDropdownOptions()) {
      this.preloadDropdownApis(this.dataSource);
    }
    this.trimInteractiveSelection();
    this.refreshConflictStatusFromDataSource();
  }

  private loadOnInitDataApi(): void {
    const config = this.resolveOnInitApiConfig();
    if (!config) return;
    if (this.data?.useWidgetData && this.globalFilterService.getWidgetData(this.data?.widgetId || '').length > 0 && config.alwaysCall !== true) {
      return;
    }

    if (!this.dataSource.length) {
      this.dataSource = [this.createEmptyRow()];
    }

    this.isOnInitDataLoading = true;
    const sub = this.callApi(config)
      .pipe(finalize(() => {
        this.isOnInitDataLoading = false;
      }))
      .subscribe((response) => {
        const hasApiUrl = !!String(config.apiUrl || config.url || '').trim();
        const sourceRows = hasApiUrl
          ? this.extractRowsFromApi(response, config)
          : (Array.isArray(config.dummyData) ? config.dummyData : []);
        const rows = this.applyRenderLimit(sourceRows.map((row) => ({ ...(row || {}) })));
        if (!rows.length && config.retainExistingOnEmpty === true) return;
        this.baseRows = rows.map((row) => this.buildRow({ ...(row || {}) }));
        this.rebuildWithMappings();
        this.refreshConflictStatusFromDataSource();
      });
    this.subs.add(sub);
  }

  private resolveOnInitApiConfig(): ApiConfig | null {
    const candidates = [this.data?.onInitDataApi, this.data?.ngOnInitApi, this.data?.dataApiOnInit, this.data?.loadDataApi, this.data?.tableDataApi];
    for (const candidate of candidates) {
      const normalized = this.normalizeApiConfig(candidate);
      if (normalized) return normalized;
    }
    if (this.data?.enableApiOnInit === true && this.data?.apiUrl) {
      return this.normalizeApiConfig({
        apiUrl: this.data.apiUrl,
        method: this.data.method || 'POST',
        payload: this.data.payload,
        responsePath: this.data.responsePath || this.data.dataResponsePath,
        responseKey: this.data.responseKey
      });
    }
    return null;
  }

  private shouldPreloadDropdownOptions(): boolean {
    return this.data?.preloadDropdownOptions === true;
  }

  private preloadDropdownApis(rows: RowData[]): void {
    const seenSharedCacheKeys = new Set<string>();
    rows.forEach((row, rowIndex) => {
      this.getConfiguredColumns().forEach((columnKey) => {
        if (!this.isDropdownColumn(columnKey, row)) return;
        const config = this.getCellConfig(row, columnKey);
        const api = this.resolveDropdownApiConfig(config);
        if (!api) return;

        const cacheKey = this.buildApiCacheKey(api, row);
        const isRowScoped = api.cacheByRow === true;
        if (!isRowScoped) {
          if (seenSharedCacheKeys.has(cacheKey)) {
            return;
          }
          if (this.dropdownOptionsByCache.has(cacheKey) || this.dropdownPendingCellKeysByCache.has(cacheKey)) {
            seenSharedCacheKeys.add(cacheKey);
            return;
          }
          seenSharedCacheKeys.add(cacheKey);
        }

        this.loadDropdownOptionsForCell(row, rowIndex, columnKey, config, api);
      });
    });
  }

  private loadDropdownOptionsForCell(row: RowData, rowIndex: number, columnKey: string, columnConfig: ColumnConfig, apiConfig: ApiConfig): void {
    const cKey = this.cellKey(rowIndex, columnKey);
    const cacheKey = this.buildApiCacheKey(apiConfig, row);

    if (this.dropdownOptionsByCache.has(cacheKey)) {
      this.dropdownOptionsByCell.set(cKey, this.dropdownOptionsByCache.get(cacheKey) || []);
      this.applyDropdownDefault(row, rowIndex, columnKey);
      return;
    }

    const isRowScoped = apiConfig.cacheByRow === true;
    const pendingForCache = this.dropdownPendingCellKeysByCache.get(cacheKey);
    if (pendingForCache) {
      if (isRowScoped) {
        pendingForCache.add(cKey);
        this.dropdownLoadingCellKeys.add(cKey);
      }
      return;
    }

    this.dropdownPendingCellKeysByCache.set(cacheKey, new Set([cKey]));
    this.dropdownLoadingCellKeys.add(cKey);

    const sub = this.callApi(apiConfig, row).subscribe((response) => {
      const options = this.extractDropdownOptionsFromApi(response, apiConfig, columnConfig);
      this.dropdownOptionsByCache.set(cacheKey, options);
      const pendingKeys = Array.from(this.dropdownPendingCellKeysByCache.get(cacheKey) || []);
      this.dropdownPendingCellKeysByCache.delete(cacheKey);

      pendingKeys.forEach((pendingCellKey) => {
        this.dropdownLoadingCellKeys.delete(pendingCellKey);
        if (isRowScoped) {
          this.dropdownOptionsByCell.set(pendingCellKey, options);
        }
        const parsed = this.parseCellKey(pendingCellKey);
        if (!parsed) {
          return;
        }

        const targetRow = this.dataSource[parsed.rowIndex];
        if (!targetRow) {
          return;
        }

        this.applyDropdownDefault(targetRow, parsed.rowIndex, parsed.columnKey);
      });
    });
    this.subs.add(sub);
  }

  private getApiOptionsForCell(
    row: RowData,
    columnKey: string,
    rowIndex: number,
    config: ColumnConfig
  ): DropdownOption[] {
    const cellKey = this.cellKey(rowIndex, columnKey);
    const fromCell = this.dropdownOptionsByCell.get(cellKey);
    if (fromCell?.length) {
      return fromCell;
    }

    const apiConfig = this.resolveDropdownApiConfig(config);
    if (!apiConfig) {
      return [];
    }

    const cacheKey = this.buildApiCacheKey(apiConfig, row);
    return this.dropdownOptionsByCache.get(cacheKey) || [];
  }

  private applyDropdownDefault(row: RowData, rowIndex: number, columnKey: string): void {
    const config = this.getCellConfig(row, columnKey);
    if (config.defaultToFirstOption !== true) return;
    const hasValue = Array.isArray(row[columnKey]) ? row[columnKey].length > 0 : String(row[columnKey] ?? '').trim() !== '';
    if (hasValue) return;
    const options = this.getDropdownOptions(row, columnKey, rowIndex);
    if (!options.length) return;
    row[columnKey] = this.isMultiSelect(columnKey, row) ? [options[0].value] : options[0].value;
    this.publishWidgetData();
  }

  private getCellConfig(row: RowData | undefined, columnKey: string): ColumnConfig {
    const base = { ...(this.columnConfigMap[columnKey] || { key: columnKey }) };
    if (!row || typeof row !== 'object') return base;

    const scoped = row?.[this.cellConfigField];
    const scopedColumn = scoped && typeof scoped === 'object' && !Array.isArray(scoped) ? (scoped as Record<string, any>)[columnKey] : undefined;
    const defaultCellConfig = row?.['cellConfig'];
    const defaultScoped =
      defaultCellConfig && typeof defaultCellConfig === 'object' && !Array.isArray(defaultCellConfig)
        ? (defaultCellConfig as Record<string, any>)[columnKey]
        : undefined;
    const direct = row?.[`${columnKey}Config`];
    const overlay = scopedColumn || defaultScoped || direct;

    if (overlay && typeof overlay === 'object' && !Array.isArray(overlay)) {
      return { ...base, ...overlay, key: columnKey };
    }
    return base;
  }

  private resolveDropdownApiConfig(config: ColumnConfig): ApiConfig | null {
    const nested = this.normalizeApiConfig(config?.dropdownApi);
    if (nested) return nested;
    return this.normalizeApiConfig({
      apiUrl: config?.apiUrl,
      method: config?.method,
      payload: config?.payload,
      responsePath: config?.responsePath,
      responseKey: config?.responseKey,
      displayField: config?.displayField,
      valueField: config?.valueField
    });
  }

  private normalizeApiConfig(source: any): ApiConfig | null {
    if (!source) return null;
    if (typeof source === 'string') {
      const url = source.trim();
      return url ? { apiUrl: url } : null;
    }
    if (typeof source !== 'object' || Array.isArray(source)) return null;
    const apiUrl = String(source.apiUrl || source.url || '').trim();
    const dummyData = this.extractDummyRows(source);
    if (!apiUrl && !dummyData.length) return null;
    return { ...source, apiUrl, dummyData: dummyData.length ? dummyData : undefined };
  }

  private resolveFooterSubmitApiConfig(config: FooterButtonConfig): FooterSubmitApiConfig | null {
    const nested = this.normalizeFooterSubmitApiConfig(config.submitApi);
    if (nested) {
      return nested;
    }

    return this.normalizeFooterSubmitApiConfig({
      apiUrl: config.apiUrl,
      method: config.method,
      payload: config.payload,
      requestDataKey: config.requestDataKey,
      insertRequestKey: config.insertRequestKey,
      rowFieldMap: config.rowFieldMap,
      includeColumns: config.includeColumns,
      excludeColumns: config.excludeColumns,
      includeCurrentFilters: config.includeCurrentFilters,
      requestParams: config.requestParams,
      queryParams: config.queryParams,
      embedQueryParams: config.embedQueryParams,
      embeddedParams: config.embeddedParams,
      rawQueryString: config.rawQueryString,
      successMessage: config.successMessage,
      errorMessage: config.errorMessage
    });
  }

  private resolveFooterSubmitApiConfigs(config: FooterButtonConfig): FooterSubmitApiConfig[] {
    const submitApi = config.submitApi;
    if (!submitApi) {
      return [];
    }

    if (Array.isArray(submitApi)) {
      return submitApi.map(api => this.normalizeFooterSubmitApiConfig(api)).filter(api => !!api) as FooterSubmitApiConfig[];
    } else {
      const normalized = this.normalizeFooterSubmitApiConfig(submitApi);
      return normalized ? [normalized] : [];
    }
  }

  private isConflictStatusButton(config: FooterButtonConfig): boolean {
    if (!config || typeof config !== 'object') {
      return false;
    }

    if (config.isConflictStatusButton === true || config.type === 'conflict-status') {
      return true;
    }

    const label = String(config.label || '').trim().toLowerCase();
    return label === 'Conflicts Remaining';
  }

  private getConflictStatusButtonConfig(): FooterButtonConfig | null {
    const config = this.resolveFooterButtonConfigs().find((buttonConfig) => this.isConflictStatusButton(buttonConfig));
    return config || null;
  }

  private refreshConflictStatusFromDataSource(): void {
    const conflictConfig = this.getConflictStatusButtonConfig();
    if (!conflictConfig) {
      this.conflictCount = 0;
      return;
    }

    this.conflictCount = this.calculateConflictCount(this.dataSource, conflictConfig);
  }

  private refreshConflictStatusFromResponse(response: any, submitApiConfig: FooterSubmitApiConfig): void {
    const conflictConfig = this.getConflictStatusButtonConfig();
    if (!conflictConfig) {
      return;
    }

    const responseList = Array.isArray(response?.responseList) ? response.responseList : [];
    const detailKey = String(submitApiConfig.responseDetailKey || '').trim();
    let detailRows: RowData[] = [];
    if (responseList.length > 0 && detailKey) {
      detailRows = responseList.flatMap((item: any) => this.extractDataRowsFromCollection(item?.[detailKey]));
    }

    if (detailRows.length > 0) {
      this.conflictCount = this.calculateConflictCount(detailRows, conflictConfig);
      return;
    }

    this.refreshConflictStatusFromDataSource();
  }

  private syncConflictFlagWithApplied(row: RowData, changedColumnKey: string): void {
    if (!row || typeof row !== 'object' || Array.isArray(row)) {
      return;
    }

    const conflictConfig = this.getConflictStatusButtonConfig();
    if (!this.shouldRequireAppliedForConflict(conflictConfig || undefined)) {
      return;
    }

    const appliedColumnKey = this.resolveAppliedColumnKey(conflictConfig || undefined);
    const changedKey = this.normalizeKey(changedColumnKey);
    if (changedKey !== this.normalizeKey(appliedColumnKey) && changedKey !== 'applied') {
      return;
    }

    const configuredAppliedTrueValues = Array.isArray(conflictConfig?.conflictAppliedTrueValues)
      ? conflictConfig.conflictAppliedTrueValues
      : ['yes', 'y', 'true', '1'];
    const appliedValue =
      row[appliedColumnKey]
      ?? row['applied']
      ?? row['applied_flag']
      ?? row['appliedFlag'];

    if (this.isConflictFlagTrue(appliedValue, configuredAppliedTrueValues)) {
      return;
    }

    const conflictColumnKey = this.resolveConflictColumnKey(conflictConfig || undefined);
    const conflictFieldKey = this.resolveConflictFieldKeyInRow(row, conflictColumnKey);
    const configuredConflictTrueValues = Array.isArray(conflictConfig?.conflictTrueValues)
      ? conflictConfig.conflictTrueValues
      : [];

    if (!this.isConflictFlagTrue(row?.[conflictFieldKey], configuredConflictTrueValues)) {
      return;
    }

    row[conflictFieldKey] = this.toFalseLikeFlagValue(row?.[conflictFieldKey]);
    if (Object.prototype.hasOwnProperty.call(row, 'conflict_reason')) {
      row['conflict_reason'] = '';
    }
  }

  private resolveConflictFieldKeyInRow(row: RowData, preferredKey: string): string {
    const candidates = [preferredKey, 'conflict_flag', 'conflictFlag', 'conflict'];
    for (const key of candidates) {
      if (!key) continue;
      if (Object.prototype.hasOwnProperty.call(row, key)) {
        return key;
      }
    }
    return preferredKey || 'conflict_flag';
  }

  private resolveConflictColumnKey(config?: FooterButtonConfig): string {
    return String(config?.conflictColumnKey || this.data?.conflictColumnKey || 'conflict_flag').trim() || 'conflict_flag';
  }

  private resolveAppliedColumnKey(config?: FooterButtonConfig): string {
    return String(config?.conflictAppliedColumnKey || this.data?.conflictAppliedColumnKey || 'applied').trim() || 'applied';
  }

  private shouldRequireAppliedForConflict(config?: FooterButtonConfig): boolean {
    const explicit = this.parseBooleanFlag(config?.conflictRequiresApplied ?? this.data?.conflictRequiresApplied);
    return explicit === true;
  }

  private isRowInConflict(row: RowData, config?: FooterButtonConfig): boolean {
    if (!row || typeof row !== 'object' || Array.isArray(row)) {
      return false;
    }

    const conflictColumnKey = this.resolveConflictColumnKey(config);
    const configuredConflictTrueValues = Array.isArray(config?.conflictTrueValues) ? config.conflictTrueValues : [];
    const conflictRawValue =
      row[conflictColumnKey]
      ?? row['conflict_flag']
      ?? row['conflictFlag']
      ?? row['conflict'];

    if (!this.isConflictFlagTrue(conflictRawValue, configuredConflictTrueValues)) {
      return false;
    }

    if (!this.shouldRequireAppliedForConflict(config)) {
      return true;
    }

    const appliedColumnKey = this.resolveAppliedColumnKey(config);
    const configuredAppliedTrueValues = Array.isArray(config?.conflictAppliedTrueValues)
      ? config.conflictAppliedTrueValues
      : ['yes', 'y', 'true', '1'];
    const appliedRawValue =
      row[appliedColumnKey]
      ?? row['applied']
      ?? row['applied_flag']
      ?? row['appliedFlag'];

    return this.isConflictFlagTrue(appliedRawValue, configuredAppliedTrueValues);
  }

  private toFalseLikeFlagValue(currentValue: any): any {
    if (typeof currentValue === 'boolean') {
      return false;
    }
    if (typeof currentValue === 'number') {
      return 0;
    }
    if (typeof currentValue === 'string') {
      const normalized = currentValue.trim().toLowerCase();
      if (normalized === 'yes' || normalized === 'y') {
        return 'No';
      }
      if (normalized === 'true') {
        return 'false';
      }
      if (normalized === '1') {
        return '0';
      }
      if (normalized === 'conflict' || normalized === 'conflicted') {
        return 'No';
      }
      return 'No';
    }
    return 'No';
  }

  private calculateConflictCount(rows: RowData[], config?: FooterButtonConfig): number {
    if (!Array.isArray(rows) || rows.length === 0) {
      return 0;
    }

    return rows.reduce((count, row) => {
      return this.isRowInConflict(row, config) ? count + 1 : count;
    }, 0);
  }

  private isConflictFlagTrue(value: any, configuredTrueValues: any[]): boolean {
    if (value === true) {
      return true;
    }

    if (typeof value === 'number') {
      return value === 1;
    }

    if (typeof value !== 'string') {
      return false;
    }

    const normalizedValue = value.trim().toLowerCase();
    if (!normalizedValue) {
      return false;
    }

    if (Array.isArray(configuredTrueValues) && configuredTrueValues.length > 0) {
      const configuredSet = new Set(
        configuredTrueValues
          .map((entry) => String(entry ?? '').trim().toLowerCase())
          .filter((entry) => entry.length > 0)
      );
      return configuredSet.has(normalizedValue);
    }

    return ['true', '1', 'yes', 'y', 'conflict', 'conflicted'].includes(normalizedValue);
  }

  private normalizeFooterSubmitApiConfig(source: any): FooterSubmitApiConfig | null {
    if (!source || typeof source !== 'object' || Array.isArray(source)) {
      return null;
    }

    const apiUrl = String(source.apiUrl || source.url || '').trim();
    if (!apiUrl) {
      return null;
    }

    return {
      ...(source as FooterSubmitApiConfig),
      apiUrl
    };
  }

  private buildFooterSubmitPayload(config: FooterSubmitApiConfig): Record<string, any> {
    const basePayload = this.toRecord(this.deepClone(config.payload ?? config.customPayload ?? {}));
    const sendDirtyRowsOnly = this.shouldSendDirtyRowsOnly(config);

    if (config.skipTableData === true && !sendDirtyRowsOnly) {
      return basePayload;
    }

    const requestDataKey = String(config.requestDataKey || 'requestData').trim() || 'requestData';
    const insertRequestKey = String(config.insertRequestKey || 'insert_request').trim() || 'insert_request';

    const requestData = this.toRecord(basePayload[requestDataKey]);
    requestData[insertRequestKey] = this.buildFooterInsertRows(config, sendDirtyRowsOnly);
    basePayload[requestDataKey] = requestData;

    return basePayload;
  }

  private buildFooterInsertRows(config: FooterSubmitApiConfig, dirtyOnly = false): RowData[] {
    const includeColumns = this.toStringArray(config.includeColumns);
    const includeKeys = includeColumns.length > 0
      ? includeColumns
      : this.getConfiguredColumns();
    const excludeKeySet = new Set(
      this.toStringArray(config.excludeColumns).map((key) => this.normalizeKey(key))
    );
    const rowFieldMap = this.toRecord(config.rowFieldMap);
    const mappedSources = new Set<string>();

    Object.entries(rowFieldMap).forEach(([targetKey, sourceKey]) => {
      const target = String(targetKey || '').trim();
      const source = String(sourceKey || '').trim();
      if (!target || !source || target === source) {
        return;
      }
      mappedSources.add(source);
    });

    const sourceRows = Array.isArray(this.dataSource) ? this.dataSource : [];
    return sourceRows
      .filter((row) => !!row && typeof row === 'object' && !Array.isArray(row))
      .filter((row) => !dirtyOnly || this.isRowDirty(row))
      .map((row) => {
        const payloadRow: RowData = {};

        includeKeys.forEach((columnKey) => {
          if (!columnKey) {
            return;
          }

          if (excludeKeySet.has(this.normalizeKey(columnKey))) {
            return;
          }

          payloadRow[columnKey] = row[columnKey];
        });

        Object.entries(rowFieldMap).forEach(([targetKey, sourceKey]) => {
          const target = String(targetKey || '').trim();
          const source = String(sourceKey || '').trim();
          if (!target || !source) {
            return;
          }
          if (excludeKeySet.has(this.normalizeKey(target))) {
            return;
          }
          payloadRow[target] = row[source];
        });

        if (
          (payloadRow['channel'] === undefined || payloadRow['channel'] === null || String(payloadRow['channel']).trim() === '')
          && row['Channel'] !== undefined
        ) {
          payloadRow['channel'] = row['Channel'];
        }

        mappedSources.forEach((sourceKey) => {
          delete payloadRow[sourceKey];
        });

        delete payloadRow[this.cellConfigField];
        delete payloadRow['cellConfig'];
        Object.keys(payloadRow).forEach((key) => {
          if (String(key).endsWith('Options')) {
            delete payloadRow[key];
          }
        });

        return payloadRow;
      });
  }

  private shouldSendDirtyRowsOnly(config: FooterSubmitApiConfig): boolean {
    return config.sendDirtyRowsOnly === true || config.onlyChangedRows === true;
  }

  private clearDirtyRows(): void {
    this.dirtyRowIds.clear();
  }

  private markRowAsDirty(row: RowData): void {
    const rowId = this.ensureInternalRowId(row);
    if (!rowId) {
      return;
    }
    this.dirtyRowIds.add(rowId);
  }

  private isRowDirty(row: RowData): boolean {
    const rowId = this.ensureInternalRowId(row);
    if (!rowId) {
      return false;
    }
    return this.dirtyRowIds.has(rowId);
  }

  private ensureInternalRowId(row: RowData): string {
    if (!row || typeof row !== 'object' || Array.isArray(row)) {
      return '';
    }
    const existing = String(row[this.internalRowIdField] ?? '').trim();
    if (existing) {
      return existing;
    }
    this.rowIdSequence += 1;
    const nextId = `row-${this.rowIdSequence}`;
    row[this.internalRowIdField] = nextId;
    return nextId;
  }

  private buildFooterSubmitRequest(
    config: FooterSubmitApiConfig,
    payload: Record<string, any>
  ): Observable<any> | null {
    const apiUrl = String(config.apiUrl || config.url || '').trim();
    if (!apiUrl) {
      return null;
    }

    const filters = config.includeCurrentFilters === true
      ? this.deepClone(this.globalFilterService.getCurrentFilters() || {})
      : { queryParams: {}, requestParams: {} };
    filters.queryParams = { ...(filters.queryParams || {}), ...(config.queryParams || {}) };
    filters.requestParams = { ...(filters.requestParams || {}), ...(config.requestParams || {}) };
    if (config.rawQueryString) {
      filters.rawQueryString = config.rawQueryString;
    }

    const apiData = {
      skipDefaultPayload: true,
      // Pass payload directly to avoid expensive deep-clone before dispatch.
      // ConfigService merges it immutably, so this does not alter behavior.
      customPayload: payload,
      embedQueryParams: config.embedQueryParams === true,
      embeddedParams: Array.isArray(config.embeddedParams) ? config.embeddedParams : undefined
    };

    return this.configService.getApi(
      apiUrl,
      filters,
      apiData,
      String(config.method || 'POST').toLowerCase()
    );
  }

  private syncFooterResponseToWidget(response: any, config: FooterSubmitApiConfig): void {
    const widgetIds = this.toStringArray(config.responseWidgetId);
    if (!widgetIds.length) {
      return;
    }

    const responseRows = this.extractFooterResponseRows(response, config);
    if (!responseRows.length) {
      return;
    }

    // Store detail rows (from responseDetailKey) grouped by quote_id for popup use
    this.storeDetailRowsForPopup(response, config, widgetIds);

    const mappedRows = responseRows.map((row) => this.mapFooterResponseRow(row, config));
    const mergeKey = String(config.mergeByKey || 'quote_id').trim();

    widgetIds.forEach((widgetId) => {
      if (!mergeKey) {
        this.globalFilterService.setWidgetData(widgetId, mappedRows.map((row) => ({ ...row })));
        return;
      }

      const existingRows = this.globalFilterService.getWidgetData(widgetId);
      const mergedByKey = new Map<string, RowData>();

      existingRows.forEach((row) => {
        const key = String(row?.[mergeKey] ?? '').trim();
        if (key) {
          mergedByKey.set(key, { ...(row || {}) });
        }
      });

      mappedRows.forEach((row) => {
        const key = String(row?.[mergeKey] ?? '').trim();
        if (key) {
          mergedByKey.set(key, { ...(row || {}) });
          return;
        }

        mergedByKey.set(`__row_${mergedByKey.size + 1}`, { ...(row || {}) });
      });

      this.globalFilterService.setWidgetData(widgetId, Array.from(mergedByKey.values()));
    });
  }

  private storeDetailRowsForPopup(response: any, config: FooterSubmitApiConfig, widgetIds: string[]): void {
    const responseList = Array.isArray(response?.responseList) ? response.responseList : [];
    if (!responseList.length) {
      return;
    }

    const detailKey = String(config.responseDetailKey || '').trim();
    if (!detailKey) {
      return;
    }

    const detailRows = responseList.flatMap((item: any) => this.extractDataRowsFromCollection(item?.[detailKey]));
    if (!detailRows.length) {
      return;
    }

    widgetIds.forEach((widgetId) => {
      this.globalFilterService.setWidgetData(`${widgetId}_details`, detailRows.map((row: RowData) => ({ ...row })));
    });
  }

  private extractFooterResponseRows(response: any, config: FooterSubmitApiConfig): RowData[] {
    const collectionRows = this.extractFooterResponseRowsFromEntityCollections(response, config);
    if (collectionRows.length > 0) {
      return collectionRows;
    }

    const extracted = this.extractApiValue(response, config);
    if (Array.isArray(extracted)) {
      return extracted
        .filter((item) => !!item && typeof item === 'object' && !Array.isArray(item))
        .map((item) => ({ ...(item as RowData) }));
    }

    if (!extracted || typeof extracted !== 'object') {
      const fallbackRows = this.extractFooterResponseRowsFromCommonShapes(response);
      return fallbackRows;
    }

    if (Array.isArray((extracted as any).data)) {
      return (extracted as any).data
        .filter((item: any) => !!item && typeof item === 'object' && !Array.isArray(item))
        .map((item: any) => ({ ...item }));
    }

    if ((extracted as any).data && typeof (extracted as any).data === 'object' && !Array.isArray((extracted as any).data)) {
      return [{ ...((extracted as any).data as RowData) }];
    }

    if (Array.isArray((extracted as any).responseList)) {
      const responseListRows = ((extracted as any).responseList as any[])
        .flatMap((item: any) => {
          if (!item) {
            return [];
          }
          if (item.data && typeof item.data === 'object' && !Array.isArray(item.data)) {
            return [{ ...(item.data as RowData) }];
          }
          if (Array.isArray(item.data)) {
            return item.data
              .filter((entry: any) => !!entry && typeof entry === 'object' && !Array.isArray(entry))
              .map((entry: any) => ({ ...(entry as RowData) }));
          }
          if (typeof item === 'object' && !Array.isArray(item)) {
            return [{ ...(item as RowData) }];
          }
          return [];
        });

      if (responseListRows.length > 0) {
        return responseListRows;
      }
    }

    return [{ ...(extracted as RowData) }];
  }

  private extractFooterResponseRowsFromEntityCollections(response: any, config: FooterSubmitApiConfig): RowData[] {
    const responseList = Array.isArray(response?.responseList) ? response.responseList : [];
    if (!responseList.length) {
      return [];
    }

    const summaryKey = String(config.responseSummaryKey || 'quote_details_discount_final').trim();
    const detailKey = String(config.responseDetailKey || 'discount_request_final').trim();
    if (!summaryKey) {
      return [];
    }

    const detailRows = responseList.flatMap((item: any) => this.extractDataRowsFromCollection(item?.[detailKey]));
    const detailByQuote = new Map<string, RowData>();
    detailRows.forEach((row: RowData) => {
      const quoteId = String(row?.['quote_id'] ?? '').trim();
      if (!quoteId) {
        return;
      }

      const existing = detailByQuote.get(quoteId);
      if (!existing) {
        detailByQuote.set(quoteId, { ...(row || {}) });
        return;
      }

      if (this.scoreFooterDetailRow(row) >= this.scoreFooterDetailRow(existing)) {
        detailByQuote.set(quoteId, { ...(row || {}) });
      }
    });

    const summaryRows = responseList.flatMap((item: any) => this.extractDataRowsFromCollection(item?.[summaryKey]));
    if (!summaryRows.length) {
      return [];
    }

    return summaryRows.map((summaryRow: RowData) => {
      const mergedRow: RowData = { ...(summaryRow || {}) };
      const quoteId = String(mergedRow?.['quote_id'] ?? '').trim();
      const detailRow = quoteId ? detailByQuote.get(quoteId) : undefined;

      const currentRegion = String(mergedRow?.['region'] ?? '').trim().toLowerCase();
      if ((!currentRegion || currentRegion === 'null') && detailRow?.['region']) {
        mergedRow['region'] = detailRow['region'];
      }

      if (
        (mergedRow['customer_id'] === undefined || mergedRow['customer_id'] === null || String(mergedRow['customer_id']).trim() === '')
        && detailRow?.['customer_id'] !== undefined
      ) {
        mergedRow['customer_id'] = detailRow['customer_id'];
      }

      if (
        (mergedRow['customer_name'] === undefined || mergedRow['customer_name'] === null || String(mergedRow['customer_name']).trim() === '')
        && detailRow?.['customer_id'] !== undefined
      ) {
        mergedRow['customer_name'] = detailRow['customer_id'];
      }

      if (
        mergedRow['quote_name'] === undefined
        || mergedRow['quote_name'] === null
        || String(mergedRow['quote_name']).trim() === ''
      ) {
        const derivedName = String(
          detailRow?.['quote_name']
          ?? detailRow?.['discount_type']
          ?? detailRow?.['applied_rule']
          ?? ''
        ).trim();
        mergedRow['quote_name'] = derivedName || (quoteId ? `Quote ${quoteId}` : '');
      }

      if (
        (mergedRow['discount_percent'] === undefined || mergedRow['discount_percent'] === null || String(mergedRow['discount_percent']).trim() === '')
        && detailRow?.['requested_discount'] !== undefined
        && String(detailRow['requested_discount']).trim() !== ''
      ) {
        mergedRow['discount_percent'] = detailRow['requested_discount'];
      }

      if (
        (mergedRow['approval_status'] === undefined || mergedRow['approval_status'] === null || String(mergedRow['approval_status']).trim() === '')
        && detailRow?.['status'] !== undefined
      ) {
        mergedRow['approval_status'] = detailRow['status'];
      }

      return mergedRow;
    });
  }

  private extractDataRowsFromCollection(source: any): RowData[] {
    if (!Array.isArray(source)) {
      return [];
    }

    return source
      .flatMap((item: any) => {
        if (!item) {
          return [];
        }
        if (item.data && typeof item.data === 'object' && !Array.isArray(item.data)) {
          return [{ ...(item.data as RowData) }];
        }
        if (typeof item === 'object' && !Array.isArray(item)) {
          return [{ ...(item as RowData) }];
        }
        return [];
      });
  }

  private scoreFooterDetailRow(row: RowData): number {
    const fields = [
      'discount_type',
      'applied_rule',
      'customer_id',
      'region',
      'item_id',
      'requested_discount',
      'status'
    ];

    return fields.reduce((score, field) => {
      const value = row?.[field];
      if (value === undefined || value === null) {
        return score;
      }
      const normalized = String(value).trim();
      return normalized.length > 0 ? score + 1 : score;
    }, 0);
  }

  private extractFooterResponseRowsFromCommonShapes(response: any): RowData[] {
    if (!response) {
      return [];
    }

    if (Array.isArray(response)) {
      return response
        .flatMap((item) => {
          if (!item) {
            return [];
          }
          if (item.data && typeof item.data === 'object' && !Array.isArray(item.data)) {
            return [{ ...(item.data as RowData) }];
          }
          if (typeof item === 'object' && !Array.isArray(item)) {
            return [{ ...(item as RowData) }];
          }
          return [];
        });
    }

    if (Array.isArray(response?.responseList)) {
      return (response.responseList as any[])
        .flatMap((item: any) => {
          if (!item) {
            return [];
          }
          if (item.data && typeof item.data === 'object' && !Array.isArray(item.data)) {
            return [{ ...(item.data as RowData) }];
          }
          if (typeof item === 'object' && !Array.isArray(item)) {
            return [{ ...(item as RowData) }];
          }
          return [];
        });
    }

    if (response?.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
      return [{ ...(response.data as RowData) }];
    }

    return [];
  }

  private mapFooterResponseRow(row: RowData, config: FooterSubmitApiConfig): RowData {
    const rowMap = this.toRecord(config.responseRowMap);
    const hasMap = Object.keys(rowMap).length > 0;
    const mappedRow: RowData = hasMap ? {} : { ...(row || {}) };

    if (hasMap) {
      Object.entries(rowMap).forEach(([targetKey, sourcePath]) => {
        const target = String(targetKey || '').trim();
        const source = String(sourcePath || '').trim();
        if (!target || !source) {
          return;
        }
        mappedRow[target] = this.getValueByPath(row, source);
      });
    }

    const discountPercentRaw = mappedRow['discount_percentage'];
    if (discountPercentRaw !== undefined && discountPercentRaw !== null) {
      const discountPercentText = String(discountPercentRaw).trim();
      if (discountPercentText.length > 0 && !discountPercentText.endsWith('%')) {
        mappedRow['discount_percentage'] = `${discountPercentText}%`;
      }
    }

    const statusRaw = mappedRow['approval_status'] ?? mappedRow['status'];
    const statusText = String(statusRaw ?? '').trim();
    if (statusText) {
      const normalizedStatus = statusText.toLowerCase();
      mappedRow['approval_status'] = normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1);
      mappedRow['approve'] = normalizedStatus === 'approved' ? 'Approved' : 'Approve';
    }

    if (mappedRow['quote_name'] === undefined || mappedRow['quote_name'] === null || String(mappedRow['quote_name']).trim() === '') {
      mappedRow['quote_name'] = String(
        mappedRow['discount_type']
        ?? mappedRow['applied_rule']
        ?? row['discount_type']
        ?? row['applied_rule']
        ?? ''
      ).trim();
    }

    return mappedRow;
  }

  private navigateAfterFooterSubmit(config: FooterSubmitApiConfig): void {
    const successRoute = String(config.successRoute || '').trim();
    if (!successRoute) {
      return;
    }

    const normalizedRoute = successRoute.startsWith('/') ? successRoute : `/${successRoute}`;
    this.router.navigateByUrl(normalizedRoute, { replaceUrl: config.replaceUrl === true });
  }

  private callApi(config: ApiConfig, _row?: RowData): Observable<any> {
    const apiUrl = String(config.apiUrl || config.url || '').trim();
    if (!apiUrl) {
      if (Array.isArray(config.dummyData) && config.dummyData.length > 0) {
        return of(config.dummyData.map((row) => ({ ...(row || {}) })));
      }
      return of(null);
    }

    const filters = config.includeCurrentFilters === true
      ? this.deepClone(this.globalFilterService.getCurrentFilters() || {})
      : { queryParams: {}, requestParams: {} };
    filters.queryParams = { ...(filters.queryParams || {}), ...(config.queryParams || {}) };
    filters.requestParams = { ...(filters.requestParams || {}), ...(config.requestParams || {}) };
    if (config.rawQueryString) {
      filters.rawQueryString = config.rawQueryString;
    }

    const payload = this.deepClone(config.customPayload ?? config.payload ?? {});
    const apiData = {
      skipDefaultPayload: true,
      customPayload: payload,
      embedQueryParams: config.embedQueryParams === true,
      embeddedParams: Array.isArray(config.embeddedParams) ? config.embeddedParams : undefined
    };

    return this.configService.getApi(apiUrl, filters, apiData, String(config.method || 'POST').toLowerCase()).pipe(
      catchError((error) => {
        console.error('[table-dropdown-api] API call failed', error);
        return of(null);
      })
    );
  }

  private extractRowsFromApi(response: any, config: ApiConfig): RowData[] {
    const extracted = this.extractApiValue(response, config);
    if (Array.isArray(extracted)) {
      return extracted.filter((item) => item && typeof item === 'object').map((item) => ({ ...item }));
    }
    if (extracted && typeof extracted === 'object') {
      if (Array.isArray((extracted as any).data)) {
        return this.extractRowsFromApi((extracted as any).data, { ...config, responsePath: '', responseKey: '' });
      }
      if (Array.isArray((extracted as any).responseList)) {
        return this.extractRowsFromApi((extracted as any).responseList, { ...config, responsePath: '', responseKey: '' });
      }
      return [{ ...extracted }];
    }
    return [];
  }

  private extractDropdownOptionsFromApi(response: any, api: ApiConfig, column: ColumnConfig): DropdownOption[] {
    const extracted = this.extractApiValue(response, api);
    const values = Array.isArray(extracted) ? extracted : (extracted !== undefined && extracted !== null ? [extracted] : []);
    return this.normalizeOptions(values, {
      displayField: api.displayField || column.displayField,
      valueField: api.valueField || column.valueField
    });
  }

  private extractApiValue(response: any, config: ApiConfig): any {
    if (response === undefined || response === null) return [];
    const path = String(config.responsePath || '').trim();
    if (path) return this.getValueByPath(response, path);
    const key = String(config.responseKey || '').trim();
    if (key) return response?.[key];
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.responseList)) return response.responseList;
    return response;
  }

  private normalizeOptions(values: any[], config?: Partial<ColumnConfig>): DropdownOption[] {
    const displayField = String((config as any)?.displayField || '').trim();
    const valueField = String((config as any)?.valueField || '').trim();
    return (Array.isArray(values) ? values : [])
      .map((item) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          const source = item as Record<string, any>;
          const value =
            (valueField ? source[valueField] : undefined) ??
            source['value'] ??
            source['id'] ??
            source['key'] ??
            source[displayField] ??
            source['label'] ??
            source['name'];
          const label =
            (displayField ? source[displayField] : undefined) ??
            source['label'] ??
            source['name'] ??
            value;
          if (value === undefined || value === null || String(value).trim() === '') return null;
          return { label: String(label ?? value).trim(), value };
        }
        if (item === undefined || item === null) return null;
        const normalized = String(item).trim();
        if (!normalized) return null;
        return { label: normalized, value: normalized };
      })
      .filter((option): option is DropdownOption => !!option && option.label.length > 0);
  }

  private extractDummyRows(source: any): RowData[] {
    const rows: unknown[] = Array.isArray(source?.dummyData) ? source.dummyData : [];
    return rows
      .filter((item): item is RowData => !!item && typeof item === 'object' && !Array.isArray(item))
      .map((item) => ({ ...item }));
  }

  private dedupeOptions(options: DropdownOption[]): DropdownOption[] {
    const seen = new Set<string>();
    const result: DropdownOption[] = [];
    options.forEach((option) => {
      const key = this.normalizeKey(String(option.value ?? option.label ?? ''));
      if (!key || seen.has(key)) return;
      seen.add(key);
      result.push(option);
    });
    return result;
  }

  private normalizeDropdownValues(rows: RowData[]): void {
    rows.forEach((row) => {
      this.getConfiguredColumns().forEach((columnKey) => {
        if (!this.isDropdownColumn(columnKey, row)) return;
        row[columnKey] = this.normalizeDropdownValue(columnKey, row[columnKey], row);
      });
    });
  }

  private normalizeDropdownValue(columnKey: string, value: any, row?: RowData): any {
    if (this.isMultiSelect(columnKey, row)) {
      if (Array.isArray(value)) return value.map((v) => String(v ?? '').trim()).filter((v) => !!v);
      if (typeof value === 'string') return value.split(',').map((v) => v.trim()).filter((v) => !!v);
      return [];
    }
    if (Array.isArray(value)) return value[0] ?? '';
    return value ?? '';
  }

  private extractFilterValues(filters: any, filterKey: string): string[] {
    const raw = filters?.requestParams?.[filterKey] ?? filters?.queryParams?.[filterKey];
    if (Array.isArray(raw)) {
      return raw.flatMap((v) => (Array.isArray(v) ? v : [v])).map((v) => String(v ?? '').trim()).filter((v) => !!v);
    }
    const normalized = String(raw ?? '').trim();
    return normalized ? [normalized] : [];
  }

  private prefillFromWorkflowCtx(rows: RowData[]): void {
    const map = this.data?.workflowCtxMap;
    if (!map || typeof map !== 'object') return;

    const ctx = this.globalFilterService.getWorkflowCtx<Record<string, any>>() || {};
    Object.entries(map as Record<string, any>).forEach(([columnKey, ctxKey]) => {
      const cKey = String(columnKey || '').trim();
      const wfKey = String(ctxKey || '').trim();
      if (!cKey || !wfKey) return;
      rows.forEach((row) => {
        const current = row[cKey];
        const empty = current === undefined || current === null || (typeof current === 'string' && current.trim() === '');
        if (!empty) return;
        const fromCtx = ctx[wfKey];
        if (fromCtx === undefined || fromCtx === null || String(fromCtx).trim() === '') return;
        row[cKey] = fromCtx;
      });
    });
  }

  private syncWorkflowCtx(columnKey: string, value: any): void {
    const map = this.data?.workflowCtxMap;
    if (!map || typeof map !== 'object') return;
    const ctxKey = String((map as Record<string, any>)[columnKey] || '').trim();
    if (!ctxKey) return;
    this.globalFilterService.setWorkflowCtx({ [ctxKey]: value });
  }

  private publishWidgetData(): void {
    if (!this.data?.publishWidgetData || !this.data?.widgetId) return;
    const rows = this.dataSource.map((row) => ({ ...row }));
    const nextSignature = this.buildRowsSignature(rows);
    this.lastPublishedWidgetDataSignature = nextSignature;
    this.suppressSelfWidgetDataSync = true;
    this.globalFilterService.setWidgetData(this.data.widgetId, rows);
    this.suppressSelfWidgetDataSync = false;
  }

  private configureInteractiveSelection(): void {
    const selection = this.resolveInteractiveSelectionConfig();
    if (selection.enabled === false) {
      this.interactiveSelectionMode = 'none';
      return;
    }
    const mode = String(selection.mode || 'none').trim().toLowerCase();
    this.interactiveSelectionMode = (mode === 'row' || mode === 'cell' || mode === 'both') ? mode : 'none';
    this.allowMultiRowSelection = selection.multiRow === true;
    this.allowMultiCellSelection = selection.multiCell === true;
  }

  private resolveInteractiveSelectionConfig(): InteractiveSelectionConfig {
    const selection = this.data?.selection;
    if (selection && typeof selection === 'object' && !Array.isArray(selection)) {
      return selection as InteractiveSelectionConfig;
    }
    return {
      enabled: this.data?.enableSelection === true || !!this.data?.interactiveSelectionMode || !!this.data?.tableSelectionMode || !!this.data?.cellSelectionMode,
      mode: this.data?.interactiveSelectionMode || this.data?.tableSelectionMode || this.data?.cellSelectionMode || 'none',
      multiRow: this.data?.multiRowSelection === true,
      multiCell: this.data?.multiCellSelection === true,
      publishToWorkflowCtx: this.data?.publishSelectionToWorkflowCtx === true,
      rowCtxKey: this.data?.selectedRowsCtxKey,
      cellCtxKey: this.data?.selectedCellsCtxKey
    };
  }

  private publishInteractiveSelection(): void {
    const config = this.resolveInteractiveSelectionConfig();
    const shouldPublish = config.publishToWorkflowCtx === true || !!String(config.rowCtxKey || '').trim() || !!String(config.cellCtxKey || '').trim();
    if (!shouldPublish) return;

    const rowCtxKey = String(config.rowCtxKey || 'selected_rows').trim();
    const cellCtxKey = String(config.cellCtxKey || 'selected_cells').trim();
    const rows = Array.from(this.selectedRowIndexes)
      .map((index) => this.dataSource[index])
      .filter((row) => !!row)
      .map((row) => ({ ...row }));

    const cells = Array.from(this.selectedCellKeys).map((key) => {
      const parsed = this.parseCellKey(key);
      if (!parsed) return null;
      const row = this.dataSource[parsed.rowIndex];
      if (!row) return null;
      return { rowIndex: parsed.rowIndex, columnKey: parsed.columnKey, value: row[parsed.columnKey], row: { ...row } };
    }).filter((cell) => !!cell);

    this.globalFilterService.setWorkflowCtx({ [rowCtxKey]: rows, [cellCtxKey]: cells });
  }

  private trimInteractiveSelection(): void {
    const maxIndex = this.dataSource.length - 1;
    this.selectedRowIndexes.forEach((index) => {
      if (index < 0 || index > maxIndex) this.selectedRowIndexes.delete(index);
    });
    this.selectedCellKeys.forEach((key) => {
      const parsed = this.parseCellKey(key);
      if (!parsed || parsed.rowIndex < 0 || parsed.rowIndex > maxIndex || !this.getConfiguredColumns().includes(parsed.columnKey)) {
        this.selectedCellKeys.delete(key);
      }
    });
    this.publishInteractiveSelection();
  }

  private dedupeRows(rows: RowData[]): RowData[] {
    const seen = new Set<string>();
    const output: RowData[] = [];
    rows.forEach((row) => {
      const signature = JSON.stringify(this.getConfiguredColumns().reduce((acc: Record<string, any>, key) => {
        acc[key] = row?.[key] ?? '';
        return acc;
      }, {}));
      if (seen.has(signature)) return;
      seen.add(signature);
      output.push(row);
    });
    return output;
  }

  private buildRowsSignature(rows: RowData[]): string {
    const columns = this.getConfiguredColumns();
    const sourceRows = Array.isArray(rows) ? rows : [];
    const normalizedRows = sourceRows.map((row) => {
      const source = row && typeof row === 'object' && !Array.isArray(row) ? row : {};
      const signatureRow: Record<string, any> = {};
      columns.forEach((columnKey) => {
        signatureRow[columnKey] = source[columnKey] ?? '';
      });
      return signatureRow;
    });
    return JSON.stringify(normalizedRows);
  }

  private normalizeNumeric(value: any): string {
    if (value === undefined || value === null) return '';
    const raw = String(value).replace(/,/g, '').trim();
    if (!raw) return '';
    if (raw.endsWith('%')) return raw;
    const sign = raw.startsWith('-') ? '-' : '';
    const unsigned = sign ? raw.slice(1) : raw;
    const [intPart, ...fractionParts] = unsigned.split('.');
    const cleanedInt = String(intPart || '').replace(/\D/g, '');
    const cleanedFraction = fractionParts.join('').replace(/\D/g, '');
    if (!cleanedInt && !cleanedFraction) return '';
    return cleanedFraction ? `${sign}${cleanedInt || '0'}.${cleanedFraction}` : `${sign}${cleanedInt}`;
  }

  private formatNumericForDisplay(value: any): string {
    if (value === undefined || value === null) return '';
    const raw = String(value).trim();
    if (!raw) return '';
    if (raw.endsWith('%')) return raw;
    const normalized = this.normalizeNumeric(raw);
    if (!normalized) return raw;
    const sign = normalized.startsWith('-') ? '-' : '';
    const unsigned = sign ? normalized.slice(1) : normalized;
    const [intPart, fraction = ''] = unsigned.split('.');
    const grouped = (intPart || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fraction ? `${sign}${grouped}.${fraction}` : `${sign}${grouped}`;
  }

  private isPercentageColumn(columnKey: string): boolean {
    const normalizedKey = String(columnKey || '').trim().toLowerCase();
    const normalizedLabel = String(this.columnLabels[columnKey] || '').trim().toLowerCase();
    return (
      normalizedKey.includes('%') ||
      normalizedLabel.includes('%') ||
      normalizedKey.includes('percent') ||
      normalizedLabel.includes('percent')
    );
  }

  private normalizePercentageForDisplay(value: any): string {
    const normalized = this.normalizeNumeric(value);
    if (!normalized) return '';

    const numericValue = Number(normalized.replace(/%$/, ''));
    if (!Number.isFinite(numericValue)) return '';

    const percentValue = numericValue > 0 && numericValue <= 1 ? numericValue * 100 : numericValue;
    const constrainedValue = Math.min(100, Math.max(1, percentValue));
    const fixedValue = Number(constrainedValue.toFixed(2));
    return String(fixedValue);
  }

  private formatPercentageForDisplay(value: any): string {
    if (value === undefined || value === null) return '';
    const raw = String(value).replace(/,/g, '').trim();
    if (!raw) return '';

    const normalized = this.normalizePercentageForDisplay(raw);
    return normalized ? `${normalized}%` : '';
  }

  isNumericColumn(columnKey: string, row?: RowData): boolean {
    const normalizedKey = String(columnKey || '').toLowerCase();
    const normalizedLabel = String(this.columnLabels[columnKey] || '').toLowerCase();
    if (this.textColumnKeys.has(normalizedKey)) return false;
    if (this.nonNumericHints.some((hint) => normalizedKey.includes(hint) || normalizedLabel.includes(hint))) return false;
    const likelyNumeric = normalizedLabel.includes('%') || normalizedKey.includes('%') || this.numericHints.some((hint) => normalizedKey.includes(hint) || normalizedLabel.includes(hint));
    if (likelyNumeric) return true;
    const value = row?.[columnKey];
    if (typeof value === 'number') return !Number.isNaN(value);
    if (typeof value !== 'string') return false;
    const cleaned = value.replace(/[%,]/g, '').trim();
    return cleaned ? !Number.isNaN(Number(cleaned)) : false;
  }

  private toCssSize(value: any): string | null {
    if (value === undefined || value === null) return null;
    if (typeof value === 'number' && !Number.isNaN(value)) return `${value}px`;
    const raw = String(value).trim();
    if (!raw) return null;
    return /^\d+(\.\d+)?$/.test(raw) ? `${raw}px` : raw;
  }

  private buildApiCacheKey(config: ApiConfig, row?: RowData): string {
    if (config.cacheKey) return String(config.cacheKey);
    const base = JSON.stringify({
      apiUrl: config.apiUrl || config.url || '',
      method: String(config.method || 'POST').toUpperCase(),
      payload: config.customPayload ?? config.payload ?? null,
      responsePath: config.responsePath || '',
      responseKey: config.responseKey || '',
      displayField: config.displayField || '',
      valueField: config.valueField || ''
    });
    if (config.cacheByRow !== true || !row) return base;
    return `${base}::${JSON.stringify(row)}`;
  }

  private getValueByPath(source: any, path: string): any {
    return String(path || '')
      .split('.')
      .map((segment) => segment.trim())
      .filter((segment) => !!segment)
      .reduce((acc, segment) => {
        if (acc === undefined || acc === null) return undefined;
        const m = segment.match(/^([^[\]]+)\[(\d+)\]$/);
        if (!m) return acc?.[segment];
        const value = acc?.[m[1]];
        return Array.isArray(value) ? value[Number(m[2])] : undefined;
      }, source);
  }

  private cellKey(rowIndex: number, columnKey: string): string {
    return `${rowIndex}::${columnKey}`;
  }

  private parseCellKey(key: string): { rowIndex: number; columnKey: string } | null {
    const [rowIndexRaw, columnKeyRaw] = String(key || '').split('::');
    const rowIndex = Number(rowIndexRaw);
    const columnKey = String(columnKeyRaw || '').trim();
    if (Number.isNaN(rowIndex) || !columnKey) return null;
    return { rowIndex, columnKey };
  }

  private normalizeKey(value: string): string {
    return String(value || '').trim().toLowerCase();
  }

  private toRecord(value: any): Record<string, any> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {};
    }
    return { ...(value as Record<string, any>) };
  }

  private toStringArray(value: any): string[] {
    if (Array.isArray(value)) {
      return value
        .map((item) => String(item ?? '').trim())
        .filter((item) => item.length > 0);
    }

    if (typeof value === 'string') {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    }

    return [];
  }

  private applyRenderLimit(rows: RowData[]): RowData[] {
    const configured = Number(this.data?.maxRowsToRender ?? this.data?.maxRowsToRenderOnLoad ?? 0);
    if (!Number.isFinite(configured) || configured <= 0) {
      return rows;
    }

    const maxRows = Math.floor(configured);
    if (rows.length <= maxRows) {
      return rows;
    }

    return rows.slice(0, maxRows);
  }

  private deepClone<T>(value: T): T {
    if (value === undefined || value === null) return value;
    return JSON.parse(JSON.stringify(value));
  }

  ngOnDestroy(): void {
    this.hideApprovalNotification();
    this.subs.unsubscribe();
  }
}
